/**
 * Core Module Routes
 * Currencies, Units, Countries, Food Ingredients, Professions, Folders, etc.
 */
import React, { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { Result, Spin } from 'antd';
import { RouteItem } from './index';
import {
  HomeOutlined, FileOutlined, PlusOutlined, DollarOutlined, DatabaseOutlined,
  FileTextOutlined, UserOutlined, FolderOutlined, AppstoreOutlined, ShopOutlined,
  EnvironmentOutlined, CompressOutlined, FileSearchOutlined, CameraOutlined,
  CloudDownloadOutlined, FileExcelOutlined, GlobalOutlined
} from '@ant-design/icons';
import { GiWeight } from 'react-icons/gi';

// Direct imports
import CorCurrenciesList from '../pages/core/CorCurrenciesList';
import CorUnitsList from '../pages/core/CorUnitsList';
import CorCountriesList from '../pages/core/CorCountriesList';
import CorFoodIngredientsList from '../pages/core/CorFoodIngredientsList';
import CorProfessionsList from '../pages/core/CorProfessionsList';
import CoreFolderAssignment from '../pages/core/folders/CoreFolderAssignment';
import CoreFolderManager from '../pages/core/folders/CoreFolderManager';
import CoreFolderUploads from '../pages/core/folders/CoreFolderUploads';
import PdfToReactFormGenerator from '../components/common/Forms/PdfToReactFormGenerator';
import CorPdfToSvgConvert from '../pages/core/CorPdfToSvgConvert';
import CormodalsDisplay from '../pages/core/CormodalsDisplay';
import KnightMove from '../pages/core/KnightMove';
import CorImageCompressor from '../pages/core/CorImageCompressor';
import CorPdfTextExtract from '../pages/core/CorPdfTextExtract';
import CorExifReader from '../pages/core/CorExifReader';
import CacheMaintenance from '../pages/core/CacheMaintenance';
import CoreApiEndpoints from '../pages/core/CoreApiEndpoints';
import CorCsvExcelImport from '../pages/core/folders/CorCsvExcelImport';
import CorXlsxSectionMapper from '../pages/core/folders/CorXlsxSectionMapper';
import CorHomeQuotes from '../pages/core/CorHomeQuotes';
import CorSystemUpdate from '../pages/core/CorSystemUpdate';
import CorGeneral from '../pages/core/CorGeneral';

const CorWhiteboard = lazy(() => import('../pages/core/CorWhiteboard'));
// Lazy: 27 ikon ailesinin tamamini iceri cekiyor (~28 MB) - ana bundle'a girmemeli
const CorReactIcons = lazy(() => import('../pages/core/CorReactIcons'));

// UnderConstruction component
const UnderConstruction = ({ title }: { title: string }) => (
  <Result status="info" title={`${title} Module`} subTitle="Under Construction." />
);

const RGiWeight: React.FC<any> = (props) =>
  React.createElement(GiWeight as unknown as React.ComponentType<any>, props) as React.ReactElement;

const withSuspense = (Component: React.LazyExoticComponent<any>) => (
  <Suspense fallback={<Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: '20%' }} />}>
    <Component />
  </Suspense>
);

export const coreRoutes: Record<string, RouteItem> = {
  core: {
    path: '/core',
    element: null,
    label: 'Core',
    showInMenu: false,
    children: {
      dashboard: {
        path: '/core/dashboard',
        element: <UnderConstruction title="Core" />,
        label: 'Dashboard',
        icon: <HomeOutlined />,
        showInMenu: true,
      },
      general: {
        path: '/core/general',
        element: <CorGeneral />,
        label: 'General',
        labelKey: 'routes.core.general',
        icon: <GlobalOutlined />,
        showInMenu: true,
        hideMenuChildren: true,
        children: {
          currencies: {
            path: '/core/general/currencies/list',
            element: <CorCurrenciesList />,
            label: 'Currencies',
            labelKey: 'routes.core.currencies',
            icon: <DollarOutlined />,
            showInMenu: true,
          },
          units: {
            path: '/core/general/units/list',
            element: <CorUnitsList />,
            label: 'Units',
            labelKey: 'routes.core.units',
            icon: <RGiWeight size={18} />,
            showInMenu: true,
          },
          countries: {
            path: '/core/general/countries/list',
            element: <CorCountriesList />,
            label: 'Countries',
            labelKey: 'routes.core.countries',
            icon: <EnvironmentOutlined />,
            showInMenu: true,
          },
          foodIngredients: {
            path: '/core/general/food-ingredients/list',
            element: <CorFoodIngredientsList />,
            label: 'Food Ingredients',
            labelKey: 'routes.core.foodIngredients',
            icon: <FileTextOutlined />,
            showInMenu: true,
          },
          professions: {
            path: '/core/general/professions/list',
            element: <CorProfessionsList />,
            label: 'Professions',
            labelKey: 'routes.core.professions',
            icon: <UserOutlined />,
            showInMenu: true,
          },
          pdfToReact: {
            path: '/core/general/pdf-to-react',
            element: <PdfToReactFormGenerator />,
            label: 'PDF to React Form',
            labelKey: 'routes.core.pdfToReact',
            icon: <FileTextOutlined />,
            showInMenu: true,
          },
          pdfToSvg: {
            path: '/core/general/pdf-to-svg',
            element: <CorPdfToSvgConvert />,
            label: 'PDF to SVG',
            labelKey: 'routes.core.pdfToSvg',
            icon: <FileOutlined />,
            showInMenu: true,
          },
          pdfTextExtract: {
            path: '/core/general/pdf-text-extract',
            element: <CorPdfTextExtract />,
            label: 'PDF Text Extract',
            labelKey: 'routes.core.pdfTextExtract',
            icon: <FileSearchOutlined />,
            showInMenu: true,
          },
          exifReader: {
            path: '/core/general/exif-reader',
            element: <CorExifReader />,
            label: 'Photo EXIF Reader',
            labelKey: 'routes.core.exifReader',
            icon: <CameraOutlined />,
            showInMenu: true,
          },
        },
      },
      folders: {
        path: '/core/folders',
        element: <CoreFolderAssignment />,
        label: 'Folder Tools',
        icon: <FolderOutlined />,
        showInMenu: true,
        grandchildren: {
          folderManager: {
            path: '/core/folders/assignments',
            element: <CoreFolderAssignment />,
            label: 'Folder Assignments',
            showInMenu: true,
          },
          makeDirectory: {
            path: '/core/folders/manager',
            element: <CoreFolderManager />,
            label: 'Folder Manager',
            icon: <PlusOutlined />,
            showInMenu: true,
          },
          uploads: {
            path: '/core/folders/uploads',
            element: <CoreFolderUploads />,
            label: 'Folder uploads',
            labelKey: 'routes.core.folderUploads',
            icon: <FolderOutlined />,
            showInMenu: true,
          },
          csvExcelImport: {
            path: '/core/folders/csv-excel-import',
            element: <CorCsvExcelImport />,
            label: 'Csv-Excel Import',
            icon: <DatabaseOutlined />,
            showInMenu: true,
          },
          xlsxSectionMapper: {
            path: '/core/folders/xlsx-section-mapper',
            element: <CorXlsxSectionMapper />,
            label: 'XLSX Section Mapper',
            icon: <FileExcelOutlined />,
            showInMenu: true,
          },
        },
      },
      pdfToReactLegacy: {
        path: '/core/pdf-to-react',
        element: <Navigate to="/core/general/pdf-to-react" replace />,
        showInMenu: false,
      },
      apiEndpoints: {
        path: '/core/api-endpoints',
        element: <CoreApiEndpoints />,
        label: 'API Endpoints',
        labelKey: 'routes.core.apiEndpoints',
        icon: <FileOutlined />,
        showInMenu: true,
      },
      pdfToSvgLegacy: {
        path: '/core/pdf-to-svg',
        element: <Navigate to="/core/general/pdf-to-svg" replace />,
        showInMenu: false,
      },
      imageCompressor: {
        path: '/core/image-compressor',
        element: <CorImageCompressor />,
        label: 'Image Compressor',
        labelKey: 'routes.core.imageCompressor',
        icon: <CompressOutlined />,
        showInMenu: true,
      },
      pdfTextExtractLegacy: {
        path: '/core/pdf-text-extract',
        element: <Navigate to="/core/general/pdf-text-extract" replace />,
        showInMenu: false,
      },
      exifReaderLegacy: {
        path: '/core/exif-reader',
        element: <Navigate to="/core/general/exif-reader" replace />,
        showInMenu: false,
      },
      modals: {
        path: '/core/modals',
        element: <CormodalsDisplay />,
        label: 'Modals',
        showInMenu: true,
      },
      knightMove: {
        path: '/core/knight-move',
        element: <KnightMove />,
        label: 'Knight Move',
        icon: <ShopOutlined />,
        showInMenu: true,
      },
      reactIconsAi: { path: '/core/react-icons/ai', element: withSuspense(CorReactIcons),
        label: 'React Icons (AI)', icon: <AppstoreOutlined />, showInMenu: true,},
      systemUpdate: {
        path: '/core/system-update',
        element: <CorSystemUpdate />,
        label: 'System Update',
        icon: <CloudDownloadOutlined />,
        showInMenu: true,
      },
      cacheMaintenance: { path: '/core/cache',   element: <CacheMaintenance />,
        label: 'Cache Maintenance', icon: <DatabaseOutlined />,  showInMenu: true,},
      homeQuotes: {
        path: '/core/home-quotes',
        element: <CorHomeQuotes />,
        label: 'Quotes',
        icon: <FileTextOutlined />,
        showInMenu: true,
      },
      whiteboard: {
        path: '/core/whiteboard',
        element: withSuspense(CorWhiteboard),
        labelKey: 'routes.core.whiteboard',
        icon: <AppstoreOutlined />,
        showInMenu: true,
      },
    },
  },
};